import './pagination.scss';

import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { InputSelect } from '../../form/input/input-select';
import { CustomFormattedMessage } from '../../intl/custom-formatted-message';

export const PaginationMaxPages = 3;

export type PaginationData = {
  page: number;
  total: number;
};

export type PaginationProps = {
  limit?: number;
  paging?: PaginationData;
  showLimit?: string[] | false;
  showInfo?: boolean;
  showPages?: boolean;
  showPaging?: boolean;
  onChangeLimit?: (value: number) => void;
  onChangePage?: (value: number) => void;
};

export function Pagination({
  limit = 10,
  showLimit = ['25', '50', '75', '100', '1000'],
  showInfo = true,
  showPages = true,
  showPaging = true,
  onChangeLimit,
  onChangePage,
  ...props
}: PaginationProps) {
  const intl = useIntl();

  const [paging, setPaging] = useState<PaginationData>({
    page: 1,
    total: 0,
  });

  useEffect(() => {
    if (props.paging) {
      setPaging(props.paging);
    }
  }, [props.paging, limit]);

  const [pages, setPages] = useState<number>(1);
  const [pagingState, setPaginaState] = useState<{
    first: number;
    last: number;
  }>({
    first: 1,
    last: 1,
  });

  useEffect(() => {
    let firstPage = Math.max(paging.page - 1, 1);

    const lastPage = Math.min(
      Math.max(1, Math.ceil(paging.total / limit)),
      firstPage + (PaginationMaxPages - 1)
    );

    firstPage = Math.max(
      1,
      Math.min(firstPage, lastPage - (PaginationMaxPages - 1))
    );

    setPages(Math.max(1, Math.ceil(paging.total / limit)));

    setPaginaState({
      first: firstPage,
      last: lastPage,
    });
  }, [paging, limit]);

  const onHandleLimit = useCallback(
    (value: string | null) => {
      onChangeLimit?.(Number(value));
    },
    [onChangeLimit]
  );

  const onHandlePage = useCallback(
    (value: number) => {
      onChangePage?.(value);
    },
    [onChangePage]
  );

  if (!showLimit && !showInfo && !showPaging && !showPages) return null;

  return (
    <div className="pagination">
      {showLimit && (
        <div className="pagination__limit">
          <InputSelect
            name="limit"
            label={<CustomFormattedMessage id="common.pagination.rows" />}
            options={showLimit.map((option: string) => ({
              key: option,
              title:
                option === '1000'
                  ? intl.formatMessage({
                      id: 'common.pagination.view-all',
                    })
                  : option,
            }))}
            value={limit?.toString()}
            onChange={onHandleLimit}
          />
        </div>
      )}

      {showInfo && (
        <div className="pagination__info">
          <p>
            <CustomFormattedMessage
              id="common.pagination.info"
              values={{
                first: paging.total === 0 ? 0 : (paging.page - 1) * limit + 1,
                last:
                  paging.total === 0
                    ? 0
                    : Math.min(paging.page * limit, paging.total),
                total: paging.total,
              }}
            />
          </p>
        </div>
      )}

      {(showPaging || showPages) && (
        <div className="pagination__content">
          <ul>
            {showPaging && (
              <>
                <li>
                  <button
                    type="button"
                    className="first"
                    disabled={pages === 1 || paging.page <= 1}
                    onClick={() => onHandlePage(1)}
                  >
                    <CustomFormattedMessage id="common.pagination.first" />
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="prev"
                    disabled={pages === 1 || paging.page <= 1}
                    onClick={() => onHandlePage(paging.page - 1)}
                  >
                    <CustomFormattedMessage id="common.pagination.prev" />
                  </button>
                </li>
              </>
            )}

            {showPages &&
              [
                ...Array.from(
                  new Array(Math.min(PaginationMaxPages, pagingState.last)),
                  (_value: number, index: number) => index + pagingState.first
                ),
              ].map((value: number) => (
                <li
                  className={value === paging.page ? 'active' : ''}
                  key={value}
                >
                  <button type="button" onClick={() => onHandlePage(value)}>
                    {value}
                  </button>
                </li>
              ))}

            {showPaging && (
              <>
                <li>
                  <button
                    type="button"
                    className="next"
                    disabled={pages === 1 || paging.page >= pages}
                    onClick={() => onHandlePage(paging.page + 1)}
                  >
                    <CustomFormattedMessage id="common.pagination.next" />
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="last"
                    disabled={pages === 1 || paging.page >= pages}
                    onClick={() => onHandlePage(pages)}
                  >
                    <CustomFormattedMessage id="common.pagination.last" />
                  </button>
                </li>
              </>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
